import React, { useEffect, useState } from 'react'
import { GeoIpData } from '../../../services'
import { MapPin, Globe, Search } from 'lucide-react'

function GeoIp() {
  const [geo, setGeo] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const fetchGeo = async () => {
    try {
      setLoading(true)
      const res = await GeoIpData()
      setGeo(res)
    } catch (error) {
      console.error('Error fetching activity logs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGeo()
  }, [])

  const filteredGeo = geo.filter(item =>
    item.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.ip?.includes(searchTerm)
  )

  const totalPages = Math.ceil(filteredGeo.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedGeo = filteredGeo.slice(startIndex, startIndex + itemsPerPage)

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 p-6 mb-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-3 rounded-lg mr-4">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Geographic IP Data</h2>
            <p className="text-sm text-gray-500">Detailed location information for all IPs</p>
          </div>
        </div>
        <div className="bg-teal-50 px-4 py-2 rounded-lg">
          <span className="text-sm text-gray-600">Total: </span>
          <span className="text-lg font-bold text-teal-600">{geo.length}</span>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by country, city, region, or IP..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
          />
        </div>
      </div>

      {paginatedGeo.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500">
            {searchTerm ? 'No matching geographic data found' : 'No geographic data found'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Country</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Region</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">City</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Coordinates</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">IP Address</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Last Checked</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGeo.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-teal-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-800">{item.country || 'N/A'}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{item.region || 'N/A'}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{item.city || 'N/A'}</td>
                    <td className="py-3 px-4 text-xs text-gray-600 font-mono">
                      {item.latitude && item.longitude 
                        ? `${parseFloat(item.latitude).toFixed(2)}, ${parseFloat(item.longitude).toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm font-mono text-gray-600">{item.ip || 'N/A'}</td>
                    <td className="py-3 px-4 text-xs text-gray-500">
                      {item.last_checked 
                        ? new Date(item.last_checked).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredGeo.length)} of {filteredGeo.length} entries
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default GeoIp